import { z } from 'zod';

// ============================================================================
// Organization Schemas
// ============================================================================

/**
 * Schema for project in organization metadata.
 *
 * `id` accepts both a UUID for explicit per-project assignments and the
 * literal `"*"` for the "all projects" wildcard mode (developer/auditor
 * org-roles see every project with their org-role applied). `role` is
 * optional because the wildcard entry derives its effective role from
 * the parent tenant.role at enforcement time.
 */
export const OrganizationProjectSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  role: z.string().optional(),
});

/**
 * Schema for organization metadata
 */
export const OrganizationMetadataSchema = z.object({
  projects: z.array(OrganizationProjectSchema).optional(),
}).catchall(z.unknown());

/**
 * Schema for organization entity
 */
export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  role: z.enum(['owner', 'administrator', 'developer', 'auditor']),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  metadata: OrganizationMetadataSchema.optional(),
});

export type OrganizationProject = z.infer<typeof OrganizationProjectSchema>;
export type OrganizationMetadata = z.infer<typeof OrganizationMetadataSchema>;
export type Organization = z.infer<typeof OrganizationSchema>;

/**
 * Schema for organization list response
 */
export const OrganizationListSchema = z.array(OrganizationSchema);

export type OrganizationList = z.infer<typeof OrganizationListSchema>;

// ============================================================================
// Organization Permissions Schemas
// ============================================================================

/**
 * Org-level permissions exposed to UI components for gating.
 * Source of truth: BFF Cedar policies (per-action). Defaults are derived
 * from the org role until `usePermissions()` overrides them with the
 * BFF response.
 */
export const OrganizationPermissionsSchema = z.object({
  canCreateProject: z.boolean(),
  canEditProject: z.boolean(),
  canDeleteProject: z.boolean(),
  canManageMembers: z.boolean(),
  canManageOrgSettings: z.boolean(),
  canViewSso: z.boolean(),
  canManageSso: z.boolean(),
});

export type OrganizationPermissions = z.infer<typeof OrganizationPermissionsSchema>;

/**
 * Project-level permissions. Today these mirror org-level for admin/owner
 * and follow project-role for non-admin org members. Used to gate UI
 * within a single project context.
 */
export const ProjectPermissionsSchema = z.object({
  canViewMetrics: z.boolean(),
  canManageSettings: z.boolean(),
  canViewTables: z.boolean(),
  canExportData: z.boolean(),
  canEditProject: z.boolean().optional(),
  canDeleteProject: z.boolean().optional(),
  // Per-project grant to view onboarding photos (front/back/selfie) in the
  // transaction detail. Owner/org-admin always; project admin/dev/auditor only
  // when explicitly granted. Enforced authoritatively by the BFF; this flag
  // gates the reveal (eye) icon in the UI.
  canViewOnboardingPhotos: z.boolean().optional(),
});

export type ProjectPermissions = z.infer<typeof ProjectPermissionsSchema>;

/**
 * Schema for combined permissions response
 */
export const PermissionsResponseSchema = z.object({
  organization: OrganizationPermissionsSchema,
  project: ProjectPermissionsSchema.optional(),
});

export type PermissionsResponse = z.infer<typeof PermissionsResponseSchema>;

// ============================================================================
// Organization Member Schemas
// ============================================================================

/**
 * Schema for organization member
 */
export const OrganizationMemberSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  organization_id: z.string().uuid(),
  role: z.enum(['owner', 'administrator', 'developer', 'auditor']),
  email: z.string().email(),
  name: z.string().optional(),
  status: z.enum(['active', 'pending', 'suspended']),
  invited_at: z.string().datetime().optional(),
  joined_at: z.string().datetime().optional(),
  last_active_at: z.string().datetime().optional(),
});

export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;

/**
 * Schema for organization members list
 */
export const OrganizationMembersListSchema = z.array(OrganizationMemberSchema);

export type OrganizationMembersList = z.infer<typeof OrganizationMembersListSchema>;

// ============================================================================
// Organization Query Schemas
// ============================================================================

/**
 * Schema for organization query parameters
 */
export const OrganizationQueryParamsSchema = z.object({
  include_permissions: z.boolean().default(false),
  include_members: z.boolean().default(false),
  include_projects_count: z.boolean().default(false),
});

export type OrganizationQueryParams = z.infer<typeof OrganizationQueryParamsSchema>;

// ============================================================================
// Organization Form Schemas
// ============================================================================

/**
 * Schema for creating a new organization
 */
export const CreateOrganizationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
  metadata: z.record(z.unknown()).optional(),
});

export type CreateOrganization = z.infer<typeof CreateOrganizationSchema>;

/**
 * Schema for updating an existing organization
 */
export const UpdateOrganizationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters').optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type UpdateOrganization = z.infer<typeof UpdateOrganizationSchema>;

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Schema for organization API error response
 */
export const OrganizationErrorSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
    field: z.string().optional(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type OrganizationError = z.infer<typeof OrganizationErrorSchema>;

/**
 * Schema for organization validation error
 */
export const OrganizationValidationErrorSchema = z.object({
  errors: z.array(z.object({
    field: z.string(),
    message: z.string(),
    code: z.string().optional(),
  })),
  message: z.string(),
});

export type OrganizationValidationError = z.infer<typeof OrganizationValidationErrorSchema>;